<template>
	<AppFormLayoutNode
		v-for="(node, index) in visibleNodes"
		:key="index"
		v-bind="{ node, schema, model }"
	/>
</template>

<script setup lang="ts">
import { FormLayoutNode } from "@q-food/q-food";

/**
 * Renders a server-defined form layout.
 *
 * The backend sends the fields as a flat list and the layout as a separate tree that references
 * those fields by name, so `schema` here is `formObjectDefinition` itself — the very same node
 * objects `AppSchemaInput` binds to. This component only looks nodes up; it never copies or
 * reshapes them, which is what keeps value hydration and write-back working.
 */
const props = defineProps<{
	nodes: FormLayoutNode[];
	schema: Record<string, any>;
	model: Record<string, any>;
}>();

const visibleNodes = computed(() => props.nodes.filter(isVisible));

/**
 * `showIf` is data, never an expression — there is no `eval` and no FormKit `$if` string involved.
 * It hides a node; it does not remove the field from the payload, exactly like the `v-if` it
 * replaces. Not an authorization mechanism.
 */
function isVisible(node: FormLayoutNode) {
	const condition = node.showIf;
	if (!condition) return true;

	const value = props.model?.[condition.field];

	if (condition.truthy != null) return condition.truthy ? !!value : !value;
	if (condition.equals !== undefined && condition.equals !== null) return value === condition.equals;
	if (condition.notEquals !== undefined && condition.notEquals !== null) return value !== condition.notEquals;

	return true;
}
</script>
